import { useState } from "react";
import type { EditProps, RaRecord } from "react-admin";
import {
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  RadioButtonGroupInput,
  Toolbar,
  SaveButton,
  useRecordContext,
  useRedirect,
  useDelete,
  useNotify,
  useRefresh,
  usePermissions,
} from "react-admin";
import { useWatch } from "react-hook-form";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { hasPermission } from "@/utils/permission-utils";
import {
  SABIL_RECEIPT_AUDIT_REMARK_HELPER,
  SABIL_RECEIPT_AUDIT_REMARK_LABEL,
  requiresSabilReceiptEditAuditRemark,
  transformSabilReceiptEdit,
  validateSabilReceiptEditAuditRemark,
} from "./sabil-receipt-edit-audit";

const ReceiptAuditRemarkInput = () => {
  const previousData = useRecordContext();
  const formValues = useWatch();

  if (!previousData || !requiresSabilReceiptEditAuditRemark(formValues, previousData)) {
    return null;
  }

  return (
    <TextInput
      source="auditRemark"
      label={SABIL_RECEIPT_AUDIT_REMARK_LABEL}
      helperText={SABIL_RECEIPT_AUDIT_REMARK_HELPER}
      fullWidth
      multiline
      minRows={2}
    />
  );
};

const SabilReceiptDeleteButton = () => {
  const record = useRecordContext<RaRecord>();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [deleteOne, { isPending }] = useDelete();

  if (!record?.id) {
    return null;
  }

  const canDelete = reason.trim().length >= 5;

  const handleDelete = () => {
    deleteOne(
      "sabilReceipt",
      { id: record.id, previousData: record, meta: { auditRemark: reason.trim() } },
      {
        onSuccess: () => {
          notify("Receipt deleted", { type: "info" });
          setOpen(false);
          redirect(
            record?.sabilId ? `/sabilData/${record.sabilId}/show/receipts` : "/sabilReceipt"
          );
          refresh();
        },
        onError: (error) => {
          const message =
            typeof error === "string"
              ? error
              : error instanceof Error
                ? error.message
                : "Delete failed";
          notify(message, { type: "error" });
        },
      }
    );
  };

  return (
    <>
      <Button color="error" onClick={() => setOpen(true)} disabled={isPending}>
        Delete
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete receipt?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting this receipt rebuilds this sabil&apos;s paid months from the remaining
            receipts. A reason is required and will appear in the sabil change history.
          </Typography>
          <TextField
            label={SABIL_RECEIPT_AUDIT_REMARK_LABEL}
            helperText={SABIL_RECEIPT_AUDIT_REMARK_HELPER}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={!canDelete || isPending}
            onClick={handleDelete}
          >
            Delete receipt
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SabilReceiptEditToolbar = () => {
  const { permissions } = usePermissions();
  const canDelete = hasPermission(permissions, "sabilReceipts.delete");

  return (
    <Toolbar sx={{ display: "flex", gap: 1, justifyContent: "space-between", flexWrap: "wrap" }}>
      <SaveButton />
      {canDelete ? <SabilReceiptDeleteButton /> : null}
    </Toolbar>
  );
};

const validateSabilReceiptEdit = (
  values: Record<string, unknown>,
  previousData?: Record<string, unknown>
) => {
  const auditError = validateSabilReceiptEditAuditRemark(values, previousData);
  return auditError ? { auditRemark: auditError } : {};
};

const SabilReceiptEdit = (props: EditProps) => (
  <Edit {...props} mutationMode="pessimistic" transform={transformSabilReceiptEdit}>
    <SimpleForm
      warnWhenUnsavedChanges
      validate={validateSabilReceiptEdit}
      toolbar={<SabilReceiptEditToolbar />}
      sx={{ maxWidth: 720 }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Changing the amount or date rebuilds this sabil&apos;s paid months from all remaining
        receipts.
      </Typography>
      <Typography variant="subtitle2" color="text.secondary">
        Receipt
      </Typography>
      <TextInput source="receiptNo" label="Receipt no." disabled fullWidth />
      <NoArrowKeyNumberInput source="amount" label="Amount" fullWidth />
      <DateInput source="receiptDate" label="Receipt date" fullWidth />
      <RadioButtonGroupInput
        source="paymentMode"
        choices={[
          { id: "CASH", name: "Cash" },
          { id: "CHEQUE", name: "Cheque" },
          { id: "ONLINE", name: "Online" },
        ]}
      />
      <TextInput source="transactionId" label="Transaction ID / Reference" fullWidth />
      <TextInput source="remarks" label="Remarks" fullWidth multiline minRows={2} />
      <ReceiptAuditRemarkInput />
    </SimpleForm>
  </Edit>
);

export default SabilReceiptEdit;
